import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Check, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const TOKEN_FN_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/handle-email-unsubscribe`;
const NEWSLETTER_FN_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/newsletter-unsubscribe`;
const ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

type State =
  | { kind: "loading" }
  | { kind: "valid" }
  | { kind: "already" }
  | { kind: "invalid" }
  | { kind: "submitting" }
  | { kind: "done" }
  | { kind: "email-form" }
  | { kind: "error"; message: string };

export default function Unsubscribe() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState<State>({ kind: "loading" });
  const [email, setEmail] = useState("");

  useEffect(() => {
    document.title = "Unsubscribe — Sorvaine";
    if (!token) {
      // No token = newsletter unsubscribe flow (footer link)
      setState({ kind: "email-form" });
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${TOKEN_FN_URL}?token=${encodeURIComponent(token)}`, {
          method: "GET",
          headers: { apikey: ANON_KEY },
        });
        const data = await res.json();
        if (data.valid) setState({ kind: "valid" });
        else if (data.reason === "already_unsubscribed") setState({ kind: "already" });
        else setState({ kind: "invalid" });
      } catch {
        setState({ kind: "error", message: "Couldn't validate link. Try again." });
      }
    })();
  }, [token]);

  const confirm = async () => {
    if (!token) return;
    setState({ kind: "submitting" });
    try {
      const res = await fetch(TOKEN_FN_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: ANON_KEY },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      if (data.success || data.reason === "already_unsubscribed") setState({ kind: "done" });
      else setState({ kind: "error", message: data.error || "Something went wrong." });
    } catch {
      setState({ kind: "error", message: "Network error. Try again." });
    }
  };

  const unsubscribeByEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setState({ kind: "submitting" });
    try {
      const res = await fetch(NEWSLETTER_FN_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: ANON_KEY },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();
      if (data.success) setState({ kind: "done" });
      else setState({ kind: "error", message: data.error || "Something went wrong." });
    } catch {
      setState({ kind: "error", message: "Network error. Try again." });
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md bg-card rounded-[10px] shadow-md p-8 text-center">
        <h1 className="text-xl font-semibold text-foreground mb-2">Email preferences</h1>

        {state.kind === "loading" && (
          <div className="flex flex-col items-center gap-3 py-6 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin" />
            <p className="text-sm">Checking your link…</p>
          </div>
        )}

        {state.kind === "email-form" && (
          <form onSubmit={unsubscribeByEmail} className="space-y-4 text-left">
            <p className="text-sm text-muted-foreground text-center">
              Enter your email to unsubscribe from the Sorvaine newsletter.
            </p>
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
            <Button type="submit" className="w-full">Unsubscribe</Button>
          </form>
        )}

        {state.kind === "valid" && (
          <>
            <p className="text-sm text-muted-foreground mb-6">
              Click below to unsubscribe from Sorvaine emails. You'll still receive essential account notifications.
            </p>
            <Button onClick={confirm} className="w-full">Confirm unsubscribe</Button>
          </>
        )}

        {state.kind === "submitting" && (
          <div className="flex flex-col items-center gap-3 py-6 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin" />
            <p className="text-sm">Processing…</p>
          </div>
        )}

        {state.kind === "done" && (
          <div className="flex flex-col items-center gap-3 py-4">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Check className="w-5 h-5 text-primary" />
            </div>
            <p className="text-sm text-foreground">You've been unsubscribed.</p>
          </div>
        )}

        {state.kind === "already" && (
          <div className="flex flex-col items-center gap-3 py-4">
            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
              <Check className="w-5 h-5 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">You're already unsubscribed.</p>
          </div>
        )}

        {state.kind === "invalid" && (
          <div className="flex flex-col items-center gap-3 py-4">
            <AlertCircle className="w-5 h-5 text-destructive" />
            <p className="text-sm text-muted-foreground">This unsubscribe link is invalid or expired.</p>
          </div>
        )}

        {state.kind === "error" && (
          <div className="flex flex-col items-center gap-3 py-4">
            <AlertCircle className="w-5 h-5 text-destructive" />
            <p className="text-sm text-muted-foreground">{state.message}</p>
          </div>
        )}
      </div>
    </main>
  );
}
